// Path: /home/jason/src/AgileView/autovision-ui/src/components/views/InferenceRealTimeSubView.tsx

import React, { useState, useCallback, useMemo } from 'react'
import { Card, Row, Col, Alert, Button, Modal } from 'react-bootstrap'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedModelId } from '../../../store/slices/modelSlice'
import { FaCog, FaExclamationTriangle, FaRedoAlt, FaTimes } from 'react-icons/fa'
import FileUploader from '../../FileUploader'
import { useGetModelQuery, useRunRealtimeInferenceMutation } from '../../../services/modelsApi'
import { RunInferenceResponse } from '../../../types/prediction'
import { getContrastYIQ } from '../../../utils'
import { useGetTagsQuery } from '../../../services/tagApi'
import InferenceRealTimeGalleryView from '../InferenceRealTimeGalleryView'
import styled from 'styled-components'
import ErrorDialog from '../../modals/ErrorDialog'
import { useGetProjectQuery } from '../../../services/projectsApi'

interface ConfidenceRanges {
  high: number
  medium: number
}

interface ConfidenceStats {
  high: number
  medium: number
  low: number
}

const DEFAULT_RANGES: ConfidenceRanges = {
  high: 0.8,
  medium: 0.5,
}

const ViewContainer = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
  overflow: hidden;
`

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 24px 24px;
  min-height: 0;

  @media (max-width: 768px) {
    padding: 8px 16px 16px;
  }

  /* Hide scrollbar for Chrome, Safari and Opera */
  ::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`

const StepCard = styled(Card)`
  background: #2a2a2a;
  border: none;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  margin-bottom: 0;
  flex: 1;
  min-height: 0;
  // border-radius: 8px;
`

const StepContent = styled.div`
  background: #2a2a2a;
  padding: 0;
  border-radius: 0 0 8px 8px;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;

  .header-controls {
    padding: 1.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    flex-shrink: 0;
    margin-bottom: 0;
  }

  .scrollable-content {
    flex: 1;
    overflow-y: auto;
    padding: 1.5rem;
    padding-bottom: 3rem;
    min-height: 0;
    display: flex;
    flex-direction: column;

    @media (max-width: 768px) {
      padding: 1.25rem;
      padding-bottom: 3rem;
    }

    ::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }

    ::-webkit-scrollbar-track {
      background: #1a1a1a;
      border-radius: 4px;
    }

    ::-webkit-scrollbar-thumb {
      background: #4a4a4a;
      border-radius: 4px;
    }

    ::-webkit-scrollbar-thumb:hover {
      background: #5a5a5a;
    }
  }

  .upload-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 0;
    padding: 2rem 0;
  }

  .upload-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .upload-content {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const InferenceRealTimeSubView: React.FC = () => {
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const [selectedClass, setSelectedClass] = useState<string | null>(null)
  const [selectedConfidenceLevel, setSelectedConfidenceLevel] = useState<
    'high' | 'medium' | 'low' | null
  >(null)
  const [showConfidenceSettings, setShowConfidenceSettings] = useState(false)
  const [confidenceRanges, setConfidenceRanges] = useState<ConfidenceRanges>(DEFAULT_RANGES)
  const [tempConfidenceRanges, setTempConfidenceRanges] = useState<ConfidenceRanges>(DEFAULT_RANGES)
  const [dropFiles, setDropFiles] = useState<File[] | null>(null)
  const [inferenceResults, setInferenceResults] = useState<RunInferenceResponse | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)
  const CARD_SIZE = 330

  const { data: selectedModel, isLoading: modelLoading } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
  })
  const { data: project } = useGetProjectQuery(selectedModel?.projectid || '', {
    skip: !selectedModel?.projectid,
  })
  const isDeployed = selectedModel?.deployment.status.toLowerCase() === 'in_service'
  const [runInference, { isLoading: inferenceLoading, isError }] = useRunRealtimeInferenceMutation()
  const { data: tags } = useGetTagsQuery(
    {
      project_id: project?.id || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !project }
  )

  // Helper function to type-check FetchBaseQueryError
  function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
    return typeof error === 'object' && error != null && 'status' in error
  }

  const handleInference = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles) {
        setInferenceResults(null)
        setDropFiles(acceptedFiles)
        const formData = new FormData()
        acceptedFiles.forEach((df) => {
          formData.append('files', df, df.name)
        })

        try {
          runInference({
            id: selectedModel?.id || '',
            data: formData,
            heatmap: true,
          })
            // .unwrap()
            .then((result) => {
              setInferenceResults(result.data || null)
            })
            .catch((err) => {
              console.log('Realtime error: ', err)
            })
        } catch (error) {
          let msg: string
          if (isFetchBaseQueryError(error)) {
            if ('data' in error && error.data) {
              msg = `Inference error: ${Object.values(error.data)[0]}`
            } else {
              msg = `Inference error: ${error.status}`
            }
          } else {
            msg = `Unexpected inference error: ${error}`
          }
          setErrorMessage(msg)
          setShowError(true)
        }
      }
    },
    [selectedModel, runInference]
  )

  const inferenceStats = useMemo(() => {
    if (!inferenceResults) return null

    const stats = inferenceResults.reduce(
      (acc, result) => {
        if (result.predictions.length > 0) {
          acc.imagesWithPredictions++
          result.predictions.forEach((pred) => {
            if (!acc.predictions[pred.class_name]) {
              acc.predictions[pred.class_name] = {
                classname: pred.class_name,
                count: 0,
                confidences: [],
                totalConfidence: 0,
              }
            }
            acc.predictions[pred.class_name].count++
            acc.predictions[pred.class_name].confidences.push(pred.confidence)
            acc.predictions[pred.class_name].totalConfidence += pred.confidence
            acc.total++
            acc.allConfidences.push(pred.confidence)
          })
        }
        return acc
      },
      {
        predictions: {} as Record<
          string,
          {
            classname: string
            count: number
            confidences: number[]
            totalConfidence: number
          }
        >,
        total: 0,
        imagesWithPredictions: 0,
        allConfidences: [] as number[],
      }
    )

    // Calculate confidence distribution
    const confidenceRanges = {
      high: stats.allConfidences.filter((c) => c >= 0.8).length,
      medium: stats.allConfidences.filter((c) => c >= 0.5 && c < 0.8).length,
      low: stats.allConfidences.filter((c) => c < 0.5).length,
    }

    return {
      predictions_per_class: Object.values(stats.predictions).map((p) => ({
        ...p,
        avgConfidence: p.totalConfidence / p.count,
      })),
      total_predictions: stats.total,
      total_images: inferenceResults.length,
      images_with_predictions: stats.imagesWithPredictions,
      confidence_distribution: confidenceRanges,
      avg_confidence: stats.allConfidences.reduce((a, b) => a + b, 0) / stats.allConfidences.length,
    }
  }, [inferenceResults])

  // Update filteredResults to include confidence level filtering
  const filteredResults = useMemo(() => {
    if (!inferenceResults) return undefined

    let results = inferenceResults

    // Filter by class if selected
    if (selectedClass) {
      results = results.filter((result) =>
        result.predictions.some((pred) => pred.class_name === selectedClass)
      )
    }

    // Filter by confidence level if selected
    if (selectedConfidenceLevel) {
      results = results.filter((result) =>
        result.predictions.some((pred) => {
          if (selectedConfidenceLevel === 'high') {
            return pred.confidence >= confidenceRanges.high
          } else if (selectedConfidenceLevel === 'medium') {
            return (
              pred.confidence >= confidenceRanges.medium && pred.confidence < confidenceRanges.high
            )
          } else {
            return pred.confidence < confidenceRanges.medium
          }
        })
      )
    }

    return results
  }, [inferenceResults, selectedClass, selectedConfidenceLevel, confidenceRanges])

  // Add click handler for confidence bars
  const handleConfidenceLevelClick = (level: 'high' | 'medium' | 'low') => {
    setSelectedConfidenceLevel(selectedConfidenceLevel === level ? null : level)
  }

  const confidenceDistribution = useMemo<ConfidenceStats | null>(() => {
    if (!inferenceStats) return null
    const ranges = confidenceRanges

    const stats = inferenceStats.predictions_per_class.reduce(
      (acc: ConfidenceStats, stat) => {
        stat.confidences.forEach((confidence) => {
          if (confidence >= ranges.high) acc.high++
          else if (confidence >= ranges.medium) acc.medium++
          else acc.low++
        })
        return acc
      },
      { high: 0, medium: 0, low: 0 }
    )

    return stats
  }, [inferenceStats, confidenceRanges])

  const handleOpenSettings = () => {
    setTempConfidenceRanges(confidenceRanges)
    setShowConfidenceSettings(true)
  }

  const handleSaveSettings = () => {
    setConfidenceRanges(tempConfidenceRanges)
    setShowConfidenceSettings(false)
  }

  const handleResetSettings = () => {
    setTempConfidenceRanges(DEFAULT_RANGES)
  }

  // Memoize the gradients
  if (!selectedModel) {
    return <div>No model data available.</div>
  }

  return (
    <ViewContainer>
      {!modelLoading && !isDeployed && (
        <Card.Body>
          <Alert variant="warning">
            <FaExclamationTriangle className="me-2" />
            Model must be deployed to perform real-time inference.
          </Alert>
        </Card.Body>
      )}

      <ScrollableContent>
        <Row className="g-4">
          {!modelLoading && isDeployed && (
            <Col xs={inferenceResults ? 8 : 12}>
              <StepCard>
                {/* <StepHeader $gradient={gradients.predictions}>Predictions</StepHeader> */}
                <StepContent>
                  <div className="scrollable-content">
                    {inferenceResults && (
                      <>
                        {inferenceResults.length > 0 && inferenceStats?.total_predictions === 0 ? (
                          <div className="d-flex flex-column justify-content-center align-items-center h-100">
                            <h2
                              className="text-center"
                              style={{ fontSize: '2.5rem', fontWeight: 'bold' }}
                            >
                              <FaExclamationTriangle className="me-3 text-warning" />
                              No Predictions
                            </h2>
                            <p className="text-muted mt-2">
                              The model did not detect any objects in the image(s)
                            </p>
                          </div>
                        ) : (
                          <InferenceRealTimeGalleryView
                            results={filteredResults}
                            dropFiles={dropFiles}
                            cardSize={CARD_SIZE}
                          />
                        )}
                      </>
                    )}
                    {isDeployed && !inferenceResults && (
                      <div className="upload-container">
                        <div className="upload-wrapper">
                          <div className="upload-content">
                            <FileUploader
                              onFileSelect={handleInference}
                              uploading={inferenceLoading}
                              numFiles={dropFiles?.length || 0}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    {isError && (
                      <Alert variant="danger" className="error-alert">
                        <FaExclamationTriangle className="me-2" />
                        Error occurred. {errorMessage}
                      </Alert>
                    )}
                  </div>
                </StepContent>
              </StepCard>
            </Col>
          )}

          {inferenceResults && (
            <Col xs={4}>
              <StepCard>
                {/* <StepHeader $gradient={gradients.statistics}>Statistics</StepHeader> */}
                <StepContent>
                  <div className="inference-stats-container h-100">
                    <div className="stats-wrapper">
                      <div className="stats-header d-flex justify-content-between align-items-center mb-4">
                        <h6 className="m-0">Inference Statistics</h6>
                        <button
                          className="reset-inference-btn"
                          disabled={!inferenceResults}
                          onClick={() => {
                            setInferenceResults(null)
                            setSelectedClass(null)
                          }}
                        >
                          <FaRedoAlt />
                          <span>Reset</span>
                        </button>
                      </div>

                      <div className="stats-grid">
                        <div className="stat-card total-predictions">
                          <div className="stat-content">
                            <div className="stat-number">{inferenceStats?.total_predictions}</div>
                            <div className="stat-label">Total Predictions</div>
                            <span className="stat-subtitle">
                              in {inferenceStats?.images_with_predictions} of{' '}
                              {inferenceStats?.total_images} images
                            </span>
                          </div>
                        </div>

                        <div className="stat-card avg-confidence">
                          <div className="stat-content">
                            <div className="stat-number">
                              {inferenceStats?.total_predictions &&
                              inferenceStats?.total_predictions > 0
                                ? `${(inferenceStats?.avg_confidence * 100).toFixed(1)}%`
                                : '0%'}
                            </div>
                            <div className="stat-label">Avg Confidence</div>
                          </div>
                        </div>
                      </div>

                      {/* Confidence Distribution */}
                      <div className="confidence-dist mt-4">
                        <div className="stat-header d-flex justify-content-between align-items-center">
                          <span>Confidence Distribution</span>
                          <button
                            className="settings-btn"
                            onClick={handleOpenSettings}
                            title="Adjust confidence thresholds"
                          >
                            <FaCog />
                          </button>
                        </div>
                        <div className="confidence-bars">
                          <div
                            className={`confidence-bar ${
                              selectedConfidenceLevel === 'high' ? 'selected' : ''
                            }`}
                            onClick={() => handleConfidenceLevelClick('high')}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="bar-label">
                              High (&gt;
                              {(confidenceRanges.high * 100).toFixed(0)}%)
                              {selectedConfidenceLevel === 'high' && (
                                <FaTimes className="ms-2" style={{ fontSize: '0.8em' }} />
                              )}
                            </div>
                            <div className="bar-wrapper">
                              <div
                                className="bar high"
                                style={{
                                  width: `${
                                    ((confidenceDistribution?.high || 0) /
                                      (inferenceStats?.total_predictions || 1)) *
                                    100
                                  }%`,
                                }}
                              />
                            </div>
                            <div className="bar-value">{confidenceDistribution?.high || 0}</div>
                          </div>

                          <div
                            className={`confidence-bar ${
                              selectedConfidenceLevel === 'medium' ? 'selected' : ''
                            }`}
                            onClick={() => handleConfidenceLevelClick('medium')}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="bar-label">
                              Med ({(confidenceRanges.medium * 100).toFixed(0)}-
                              {(confidenceRanges.high * 100).toFixed(0)}%)
                              {selectedConfidenceLevel === 'medium' && (
                                <FaTimes className="ms-2" style={{ fontSize: '0.8em' }} />
                              )}
                            </div>
                            <div className="bar-wrapper">
                              <div
                                className="bar medium"
                                style={{
                                  width: `${
                                    ((confidenceDistribution?.medium || 0) /
                                      (inferenceStats?.total_predictions || 1)) *
                                    100
                                  }%`,
                                }}
                              />
                            </div>
                            <div className="bar-value">{confidenceDistribution?.medium || 0}</div>
                          </div>

                          <div
                            className={`confidence-bar ${
                              selectedConfidenceLevel === 'low' ? 'selected' : ''
                            }`}
                            onClick={() => handleConfidenceLevelClick('low')}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="bar-label">
                              Low (&lt;
                              {(confidenceRanges.medium * 100).toFixed(0)}%)
                              {selectedConfidenceLevel === 'low' && (
                                <FaTimes className="ms-2" style={{ fontSize: '0.8em' }} />
                              )}
                            </div>
                            <div className="bar-wrapper">
                              <div
                                className="bar low"
                                style={{
                                  width: `${
                                    ((confidenceDistribution?.low || 0) /
                                      (inferenceStats?.total_predictions || 1)) *
                                    100
                                  }%`,
                                }}
                              />
                            </div>
                            <div className="bar-value">{confidenceDistribution?.low || 0}</div>
                          </div>
                        </div>
                      </div>

                      {/* Confidence Settings Modal */}
                      <Modal
                        show={showConfidenceSettings}
                        onHide={() => setShowConfidenceSettings(false)}
                        centered
                        className="confidence-settings-modal"
                      >
                        <Modal.Header closeButton>
                          <Modal.Title>Adjust Confidence Thresholds</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                          <div className="confidence-settings">
                            <div className="setting-group">
                              <label>High Confidence Threshold</label>
                              <div className="d-flex align-items-center gap-2">
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  value={tempConfidenceRanges.high * 100}
                                  onChange={(e) =>
                                    setTempConfidenceRanges((prev) => ({
                                      ...prev,
                                      high: Number(e.target.value) / 100,
                                    }))
                                  }
                                  className="form-range"
                                />
                                <span className="value">
                                  {(tempConfidenceRanges.high * 100).toFixed(0)}%
                                </span>
                              </div>
                            </div>
                            <div className="setting-group">
                              <label>Medium Confidence Threshold</label>
                              <div className="d-flex align-items-center gap-2">
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  value={tempConfidenceRanges.medium * 100}
                                  onChange={(e) =>
                                    setTempConfidenceRanges((prev) => ({
                                      ...prev,
                                      medium: Number(e.target.value) / 100,
                                    }))
                                  }
                                  className="form-range"
                                />
                                <span className="value">
                                  {(tempConfidenceRanges.medium * 100).toFixed(0)}%
                                </span>
                              </div>
                            </div>
                            <div className="mt-3 d-flex justify-content-end gap-2">
                              <Button variant="secondary" onClick={handleResetSettings}>
                                Reset to Defaults
                              </Button>
                              <Button variant="primary" onClick={handleSaveSettings}>
                                Save Changes
                              </Button>
                            </div>
                          </div>
                        </Modal.Body>
                      </Modal>

                      {/* Class Distribution */}
                      <div className="class-distribution mt-4">
                        <div className="distribution-header">
                          Class Distribution
                          {selectedClass && (
                            <button
                              className="clear-filter-btn ms-2"
                              onClick={() => setSelectedClass(null)}
                            >
                              <FaTimes />
                              Clear Filter
                            </button>
                          )}
                        </div>
                        <div className="predictions-by-class">
                          {inferenceStats?.predictions_per_class?.map((stat, index) => {
                            const tag = tags?.tags?.find(
                              (t) => t.name === stat.classname || t.id === stat.classname
                            )
                            const backgroundColor = tag
                              ? `#${tag.color.toString(16).padStart(6, '0')}`
                              : '#666666'
                            const textColor = getContrastYIQ(backgroundColor)
                            const isSelected = selectedClass === stat.classname

                            return (
                              <div
                                key={index}
                                className="class-stat-card"
                                onClick={() => setSelectedClass(isSelected ? null : stat.classname)}
                                style={{ cursor: 'pointer' }}
                              >
                                <div
                                  className={`class-stat ${isSelected ? 'selected' : ''}`}
                                  style={{
                                    backgroundColor,
                                    color: textColor,
                                    border: isSelected ? `2px solid ${textColor}` : 'none',
                                  }}
                                >
                                  <span className="class-name">{tag?.name || 'unk'}</span>
                                  <div className="class-metrics">
                                    <span
                                      className="class-count"
                                      style={{
                                        backgroundColor: textColor,
                                        color: backgroundColor,
                                      }}
                                    >
                                      {stat.count}
                                    </span>
                                    <span
                                      className="class-confidence"
                                      style={{
                                        backgroundColor: textColor,
                                        color: backgroundColor,
                                      }}
                                    >
                                      {(stat.avgConfidence * 100).toFixed(0)}%
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </StepContent>
              </StepCard>
            </Col>
          )}
        </Row>
      </ScrollableContent>
      <ErrorDialog show={showError} onHide={() => setShowError(false)} message={errorMessage} />
    </ViewContainer>
  )
}

export default InferenceRealTimeSubView
